import React from 'react';
import { formatDateTimeEST } from '../utils/dateUtils';

interface DashboardTableProps {
  context: 'users' | 'events' | 'customers' | 'accounts' | 'event-customers' | 'config';
  data: any[];
  onCustomerNameClick?: (row: any) => void;
}

const formatValue = (col: any, row: any, context: string) => {
  let value = row[col.key];
  
  // Format date columns to EST
  if (col.key.toLowerCase().includes('event_start_date') || col.key.toLowerCase().includes('updated_at') || col.key.toLowerCase().includes('created_at') || col.key.toLowerCase().includes('transaction_date')) {
    if (value) {
      value = formatDateTimeEST(value);
    }
  }
  
  // Custom coloring for registration and is_disabled columns
  if (col.key === 'is_disabled') {
    value = value === false || value === 'No'
      ? <span className="text-green-600 font-bold">No</span>
      : <span className="text-red-600 font-bold">Yes</span>;
  } else if (col.key === 'registration') {
    value = value === true || value === 'Yes'
      ? <span className="text-green-600 font-bold">Yes</span>
      : <span className="text-red-600 font-bold">No</span>;
  } else if (typeof value === 'boolean') {
    value = value ? 'Yes' : 'No';
  } else if (value === true) {
    value = 'Yes';
  } else if (value === false) {
    value = 'No';
  }
  
  // Format and color amount for accounts context
  if (context === 'accounts' && col.key === 'amount') {
    if (row.type && typeof value === 'number') {
      const formatted = value.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
      value = row.type.toLowerCase().includes('income')
        ? <span className="text-green-600 font-bold">{formatted}</span>
        : <span className="text-red-600 font-bold">{formatted}</span>;
    }
  }
  
  return value;
};

const columnsMap = {
  users: [
    { key: 'username', label: 'UserName' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'is_disabled', label: 'Disabled' },
    { key: 'registration', label: 'Registration' },
    { key: 'created_at', label: 'Created At' },
    { key: '_actions', label: '' }, // uuid is not shown as a column
  ],
  events: [
    { key: 'title', label: 'Title', colClassName: '' },
    { key: 'event_type', label: 'Type', colClassName: 'w-28' },
    { key: 'is_disabled', label: 'Disabled', colClassName: 'w-24' },
    { key: 'organizer_name', label: 'Organizer Name', colClassName: 'w-40' },
    { key: 'event_start_date', label: 'Start Date', colClassName: 'w-52' },
    { key: '_actions', label: '', colClassName: 'w-32' },
  ],
  customers: [
    { key: 'customer_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'created_at', label: 'Created At' },
    { key: '_actions', label: '' },
  ],
  accounts: [
    { key: 'type', label: 'Type' },
    { key: 'description', label: 'Description' },
    { key: 'from_to', label: 'From/To' },
    { key: 'amount', label: 'Amount' },
    { key: 'transaction_date', label: 'Transaction Date' },
    { key: '_actions', label: '' },
  ],
  'event-customers': [
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'email', label: 'Email' },
    { key: 'mode', label: 'Mode' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'number_of_tickets_adults', label: 'Adult Tickets' },
    { key: 'number_of_tickets_kids', label: 'Kid Tickets' },
    { key: 'ticket_emailed', label: 'Emailed' },
    { key: '_actions', label: '' },
  ],
  'config': [
    { key: 'key', label: 'Key' },
    { key: 'value', label: 'Value' },
    { key: 'created_at', label: 'Created At' },
    { key: '_actions', label: '' },
  ],
};

const DashboardTable: React.FC<DashboardTableProps> = ({ context, data, onCustomerNameClick }) => {
  const columns = columnsMap[context];
 
  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden lg:block w-full overflow-x-auto rounded-lg shadow-lg bg-white">
        <table className="min-w-full divide-y divide-primary-200">
          <thead className="bg-primary-50">
            <tr>
              {columns.map((col) => (
                <th 
                  key={col.key} 
                  className={`px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider ${
                    (col as any).colClassName !== undefined
                      ? (col as any).colClassName
                      : col.key === '_actions'
                        ? context === 'event-customers'
                          ? 'w-32'
                          : 'w-20'
                        : ''
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-primary-100">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center text-secondary-400 text-lg">
                  No results found.
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={idx} className="hover:bg-primary-50 transition">
                  {columns.map((col) => {
                    const value = formatValue(col, row, context);
                    const colWidth = (col as any).colClassName !== undefined
                      ? (col as any).colClassName
                      : col.key === '_actions'
                        ? context === 'event-customers'
                          ? 'w-32'
                          : 'w-20'
                        : '';
                    let cellClass =
                      col.key === 'is_disabled' || col.key === 'registration'
                        ? `px-6 py-4 whitespace-nowrap text-secondary-700 font-medium text-center ${colWidth}`
                        : col.key === '_actions'
                        ? `px-6 py-4 text-secondary-700 font-medium ${colWidth}`
                        : `px-6 py-4 whitespace-nowrap text-secondary-700 font-medium ${colWidth}`;

                    if (context === 'accounts' && col.key === 'amount') {
                      cellClass += ' text-right';
                    }

                    return (
                      <td key={col.key} className={cellClass}>
                        {col.key === 'customer_name' && context === 'event-customers' && onCustomerNameClick ? (
                          <button
                            onClick={() => onCustomerNameClick(row)}
                            className="text-primary-600 hover:text-primary-800 hover:underline font-semibold transition-colors"
                          >
                            {value}
                          </button>
                        ) : (
                          value
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {data.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center text-secondary-400 text-lg">
            No results found.
          </div>
        ) : (
          data.map((row, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-4 space-y-2">
              {columns.filter(col => col.key !== '_actions').map((col) => {
                const value = formatValue(col, row, context);
                return (
                  <div key={col.key} className="flex justify-between items-start border-b border-primary-100 pb-2 last:border-0">
                    <span className="text-xs font-bold text-primary-700 uppercase mr-2 flex-shrink-0">
                      {col.label}:
                    </span>
                    <span className="text-sm text-secondary-700 font-medium text-right break-words">
                      {value}
                    </span>
                  </div>
                );
              })}
              {row._actions && (
                <div className="pt-2 border-t border-primary-200">
                  {row._actions}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default DashboardTable;
